import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
console.log('🔧 Backend URL from env:', BACKEND_URL);

// Create axios instance with default config
const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Helper to get token from both storages (prioritize sessionStorage for security)
const getAuthToken = () => {
  return sessionStorage.getItem('authToken') || localStorage.getItem('authToken');
};

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle responses and errors
api.interceptors.response.use(
  (response) => {
    console.log('📡 API Response:', response.data);
    return response; // Return full response object, not just data
  },
  (error) => {
    console.error('📡 API Error:', error);
    // Normalize FastAPI 422 validation errors: `detail` can be an array of
    // {type, loc, msg, input, url} objects (or an object). Coerce to a readable
    // string so consumers that render `detail` never crash (React error #31).
    const d = error.response?.data?.detail;
    if (d != null && typeof d !== 'string') {
      try {
        if (Array.isArray(d)) {
          error.response.data.detail = d
            .map((it) => (it && typeof it === 'object'
              ? `${Array.isArray(it.loc) ? it.loc.join('.') + ': ' : ''}${it.msg || JSON.stringify(it)}`
              : String(it)))
            .join('; ');
        } else if (typeof d === 'object') {
          error.response.data.detail = d.msg || JSON.stringify(d);
        }
      } catch (_) {
        error.response.data.detail = 'Validation error';
      }
    }
    if (error.response?.status === 401) {
      // Clear token from both storages and redirect to login
      sessionStorage.removeItem('authToken');
      sessionStorage.removeItem('user');
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (userData) => api.post('/auth/register', userData),
  login: (credentials) => api.post('/auth/login', credentials),
  getCurrentUser: () => api.get('/auth/me'),
  updateProfile: (userData) => api.put('/auth/me', userData),
  logout: () => api.post('/auth/logout'),
};

// Competition API
export const competitionAPI = {
  getCompetitions: (params = {}) => api.get('/competitions/', { params }),
  getActiveCompetition: () => api.get('/competitions/active'),
  getCompetition: (id) => api.get(`/competitions/${id}`),
  getCompetitionSubmissions: (id, params = {}) => api.get(`/competitions/${id}/submissions`, { params }),
  getCompetitionStats: (id) => api.get(`/competitions/${id}/stats`),
  createCompetition: (data) => api.post('/competitions/', data),
  updateCompetition: (id, data) => api.put(`/competitions/${id}`, data),
  deleteCompetition: (id) => api.delete(`/competitions/${id}`),
};

// Submission API
export const submissionAPI = {
  getSubmissions: (params = {}) => api.get('/submissions/', { params }),
  getMySubmissions: () => api.get('/submissions/my-submissions'),
  getSubmission: (id) => api.get(`/submissions/${id}`),
  createSubmission: (data) => api.post('/submissions/', data),
  updateSubmission: (id, data) => api.put(`/submissions/${id}`, data),
  deleteSubmission: (id) => api.delete(`/submissions/${id}`),
  uploadImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/submissions/upload-image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  likeSubmission: (id) => api.post(`/submissions/${id}/like`),
};

// User API
export const userAPI = {
  getUsers: (params = {}) => api.get('/users/', { params }),
  getUser: (id) => api.get(`/users/${id}`),
  getUserSubmissions: (id, params = {}) => api.get(`/users/${id}/submissions`, { params }),
  getUserStats: (id) => api.get(`/users/${id}/stats`),
  updateUser: (id, data) => api.put(`/users/${id}`, data),
  deleteUser: (id) => api.delete(`/users/${id}`),
  makeUserAdmin: (id) => api.post(`/users/${id}/make-admin`),
  makeUserJudge: (id) => api.post(`/users/${id}/make-judge`),
};

// Gallery API
export const galleryAPI = {
  getGallerySubmissions: (params = {}) => api.get('/gallery/', { params }),
  getFeaturedSubmissions: (params = {}) => api.get('/gallery/featured', { params }),
  getCategoryStats: () => api.get('/gallery/categories'),
  getCountryStats: () => api.get('/gallery/countries'),
  getSubmissionYears: () => api.get('/gallery/years'),
  getPhotographerGallery: (id, params = {}) => api.get(`/gallery/photographer/${id}`, { params }),
  getPhotographerGalleryWithPending: (id, params = {}) => api.get(`/gallery/photographer/${id}`, { params: { ...params, include_pending: true } }),
};

// Admin API
export const adminAPI = {
  getAdminStats: () => api.get('/admin/stats'),
  getPendingSubmissions: () => api.get('/admin/pending-submissions'),
  approveSubmission: (id) => api.post(`/admin/submissions/${id}/approve`),
  rejectSubmission: (id, reason) => api.post(`/admin/submissions/${id}/reject`, { reason }),
  featureSubmission: (id) => api.post(`/admin/submissions/${id}/feature`),
  setSubmissionWinner: (id, award) => api.post(`/admin/submissions/${id}/set-winner`, { award }),
  getSubmissionsByCategory: (competitionId) => api.get('/admin/analytics/submissions-by-category', {
    params: { competition_id: competitionId }
  }),
  getSubmissionsByCountry: (competitionId) => api.get('/admin/analytics/submissions-by-country', {
    params: { competition_id: competitionId }
  }),
  getSubmissionsOverTime: (competitionId) => api.get('/admin/analytics/submissions-over-time', {
    params: { competition_id: competitionId }
  }),
  bulkApproveSubmissions: (submissionIds) => api.post('/admin/bulk-actions/approve-submissions', {
    submission_ids: submissionIds
  }),
  bulkRejectSubmissions: (submissionIds, reason) => api.post('/admin/bulk-actions/reject-submissions', {
    submission_ids: submissionIds,
    reason
  }),
};

// Payment API
export const paymentAPI = {
  createPaymentLink: (data) => api.post('/payments/create-payment-link', data),
  getPaymentStatus: (paymentLinkId) => api.get(`/payments/status/${paymentLinkId}`),
  getUserPaymentStatus: (competitionId, participantEmail) => 
    api.get('/payments/user-payment-status', { 
      params: { competition_id: competitionId, participant_email: participantEmail } 
    }),
  activateSpotterCredits: (data) => api.post('/payments/activate-spotter-credits', data),
};

export const judgeAPI = {
  // Public endpoints
  getPublicJudges: async () => api.get('/judges/public'),
  
  // Admin endpoints
  getAllJudges: async () => api.get('/judges/'),
  getJudgeById: async (id) => api.get(`/judges/${id}`),
  createJudge: async (data) => api.post('/judges/', data),
  updateJudge: async (id, data) => api.put(`/judges/${id}`, data),
  deleteJudge: async (id) => api.delete(`/judges/${id}`),
  cleanupAllJudges: async () => api.delete('/judges/cleanup-all'),
  migratePhotoUrls: async () => api.post('/judges/migrate-photo-urls'),
};

// Admin Sync API
export const adminSyncAPI = {
  exportData: () => api.get('/admin/sync/export-data'),
};

// Upload API
export const uploadAPI = {
  uploadJudgePhoto: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload/judge-photo', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  deleteJudgePhoto: async (photoUrl) => api.delete('/upload/judge-photo', { params: { photo_url: photoUrl } }),
};

// Utility functions with sessionStorage strategy for better security
export const setAuthToken = (token, rememberMe = false) => {
  // Use sessionStorage by default (cleared when browser closes)
  // Use localStorage only if "remember me" is enabled
  if (rememberMe) {
    localStorage.setItem('authToken', token);
  } else {
    sessionStorage.setItem('authToken', token);
  }
};

export const clearAuthToken = () => {
  sessionStorage.removeItem('authToken');
  sessionStorage.removeItem('user');
  localStorage.removeItem('authToken');
  localStorage.removeItem('user');
};

export const getCurrentUser = () => {
  try {
    // Check sessionStorage first, then localStorage
    const userStr = sessionStorage.getItem('user') || localStorage.getItem('user');
    if (!userStr || userStr === 'undefined' || userStr === 'null') {
      return null;
    }
    return JSON.parse(userStr);
  } catch (error) {
    console.warn('Error parsing stored user data:', error);
    // Clear invalid data from both storages
    sessionStorage.removeItem('user');
    localStorage.removeItem('user');
    return null;
  }
};

export const setCurrentUser = (user, rememberMe = false) => {
  const userStr = JSON.stringify(user);
  if (rememberMe) {
    localStorage.setItem('user', userStr);
  } else {
    sessionStorage.setItem('user', userStr);
  }
};

// News API
export const newsAPI = {
  getNews: () => api.get('/news/'),
  getNewsById: (id) => api.get(`/news/${id}`),
  getAllNews: () => api.get('/news/admin/all'),
  createNews: (data) => api.post('/news/', data),
  updateNews: (id, data) => api.put(`/news/${id}`, data),
  deleteNews: (id) => api.delete(`/news/${id}`),
  togglePublish: (id) => api.patch(`/news/${id}/publish`),
  moveArticle: (id, direction) => api.patch(`/news/${id}/move/${direction}`),
};

export default api;
export const passwordResetAPI = {
  requestReset: (email) => api.post('/password-reset/request', { email }),
  verifyToken: (token) => api.post('/password-reset/verify-token', null, { params: { token } }),
  resetPassword: (data) => api.post('/password-reset/reset', data),
};

// Contact Form API
export const contactAPI = {
  submitContactForm: (data) => api.post('/contact/submit', data),
  subscribeNewsletter: (data) => api.post('/contact/newsletter/subscribe', data),
};

// Stats API
export const statsAPI = {
  getStats: () => api.get('/stats'),
};

// RAW upload module API (Phase 3)
export const rawAPI = {
  // member
  myUploads: () => api.get('/raw/my-uploads'),
  presign: (data) => api.post('/raw/upload-presigned', data),
  confirm: (data) => api.post('/raw/confirm-upload', data),
  // admin validation
  adminList: (params = {}) => api.get('/admin/raw/list', { params }),
  adminValidate: (id) => api.post(`/admin/raw/${id}/validate`),
  adminReject: (id, reason) => api.post(`/admin/raw/${id}/reject`, { reason }),
  adminBulkValidate: (data = {}) => api.post('/admin/raw/bulk-validate', data),
  adminAccessLog: (params = {}) => api.get('/admin/raw/access-log', { params }),
  adminPreviewUrl: (id) => api.get(`/admin/raw/${id}/preview-url`),
  adminDownloadUrl: (id) => api.get(`/admin/raw/${id}/download-url`),
  adminDeadlineCheck: (dry_run) => api.post('/admin/raw/run-deadline-check', { dry_run }),
  manualDqBackfill: (submission_id, reason, dry_run) => api.post('/admin/raw/manual-dq-backfill', { submission_id, reason, dry_run }),
  repairPromotedStatus: (dry_run) => api.post('/admin/raw/repair-promoted-status', { dry_run }),
  diagnoseUploadAccess: (submission) => api.get('/admin/raw/diagnose-upload-access', { params: { submission } }),
  diagnoseImage: (submission) => api.get('/admin/raw/diagnose-image', { params: { submission } }),
  reverseCollectiveDq: (submission_id, reason, dry_run) => api.post('/admin/judging/dq/reverse', { submission_id, reason, dry_run }),
  categoriesRanking: () => api.get('/admin/judging/categories-ranking'),
  adminExtend: (id, payload) => api.post(`/admin/raw/${id}/extend-deadline`, payload),
  // retry EXIF strip (background job)
  retryStrip: (id) => api.post(`/admin/raw/${id}/retry-strip`),
  retryAllErrors: () => api.post('/admin/raw/retry-all-errors'),
  retryJob: (jobId) => api.get(`/admin/raw/retry-job/${jobId}`),
  // production toolkit (admin-driven, no CLI)
  configStatus: () => api.get('/admin/raw/config-status'),
  buildVersion: () => api.get('/admin/raw/build-version'),
  createTestFinalist: (data) => api.post('/admin/raw/test-finalist', data),
  listTestFinalists: () => api.get('/admin/raw/test-finalists'),
  cleanupTestFinalist: (id) => api.delete(`/admin/raw/test-finalist/${id}`),
  sendTestEmail: (data) => api.post('/admin/finalist/send-test', data),
  finalistStatus: () => api.get('/admin/finalist/status'),
  sendReminderMissing: () => api.post('/admin/finalist/send-reminder-missing'),
  // judge (phase 3)
  judgePreview: (id) => api.post(`/judging/phase3/request-raw-preview/${id}`),
  judgeDownload: (id) => api.post(`/judging/phase3/request-raw-download/${id}`),
  // finalist emails / groups
  sendAll: () => api.post('/admin/finalist/send-all'),
  sendJob: (jobId) => api.get(`/admin/finalist/send-job/${jobId}`),
  resendEmail: (id) => api.post(`/admin/finalist/resend/${id}`),
  sendReminder: () => api.post('/admin/finalist/send-reminder'),
  sendPromoted: (id) => api.post(`/admin/finalist/send-promoted/${id}`),
  sendFinalistEmail: (id, kind) => api.post(`/admin/finalist/send-one/${id}`, { kind }),
  syncGroups: () => api.post('/admin/finalist/sync-groups'),
};

// Public + admin galleries reveal (Chantier Galerie B1/B2)
export const galleryRevealAPI = {
  publicFinalists: () => api.get('/gallery/finalists'),
  status: () => api.get('/admin/gallery/reveal-status'),
  reveal: () => api.post('/admin/gallery/reveal-featured'),
  unreveal: () => api.post('/admin/gallery/unreveal-featured'),
};

// Gallery category sponsors (Chantier Galerie A)
export const gallerySponsorsAPI = {
  publicFor: (category, photographer_category) =>
    api.get('/gallery/sponsors', { params: { category, photographer_category } }),
  listAll: () => api.get('/gallery/sponsors/all'),
  list: () => api.get('/admin/gallery-sponsors'),
  create: (payload) => api.post('/admin/gallery-sponsors', payload),
  update: (id, payload) => api.patch(`/admin/gallery-sponsors/${id}`, payload),
  remove: (id) => api.delete(`/admin/gallery-sponsors/${id}`),
  seedDefaults: () => api.post('/admin/gallery-sponsors/seed-defaults'),
};


// Winners Reveal (2026) — public gallery (gated by awards_publicly_revealed)
export const winnersPublicAPI = {
  hub: (adminPreview = false) => api.get('/gallery/winners', { params: adminPreview ? { admin_preview: true } : {} }),
  byCategory: (categorySlug, adminPreview = false) =>
    api.get(`/gallery/winners/${categorySlug}`, { params: adminPreview ? { admin_preview: true } : {} }),
};

// Winners Reveal — admin reveal / assets / send / sponsors
export const winnersAdminAPI = {
  revealStatus: () => api.get('/admin/winners/reveal-status'),
  reveal: () => api.post('/admin/winners/reveal'),
  unreveal: () => api.post('/admin/winners/unreveal'),
};

export const winnerAssetsAPI = {
  status: () => api.get('/admin/winner-assets/status'),
  withAssets: () => api.get('/admin/winner-assets/with-assets'),
  byTheme: () => api.get('/admin/winner-assets/by-theme'),
  mappingDryRun: () => api.get('/admin/winner-assets/mapping-dry-run'),
  generateStart: (body) => api.post('/admin/winner-assets/generate/start', body),
  generateStatus: () => api.get('/admin/winner-assets/generate/status'),
  preview: (submissionId) => api.get(`/admin/winner-assets/preview/${submissionId}`),
  ogPreview: () => api.get('/admin/winner-assets/og-preview'),
  sendReveal: (body) => api.post('/admin/winner-assets/send-reveal', body),
};

export const winnerSponsorsAPI = {
  listProfiles: () => api.get('/admin/sponsor-profiles'),
  createProfile: (payload) => api.post('/admin/sponsor-profiles', payload),
  updateProfile: (id, payload) => api.patch(`/admin/sponsor-profiles/${id}`, payload),
  removeProfile: (id) => api.delete(`/admin/sponsor-profiles/${id}`),
  listMapping: () => api.get('/admin/winner-sponsors'),
  upsertMapping: (payload) => api.post('/admin/winner-sponsors', payload),
  updateMapping: (id, payload) => api.patch(`/admin/winner-sponsors/${id}`, payload),
  removeMapping: (id) => api.delete(`/admin/winner-sponsors/${id}`),
  syncFromAwards: () => api.post('/admin/winner-sponsors/sync-from-awards'),
  seed2026: () => api.post('/admin/winner-sponsors/seed-2026'),
  updatePrizes20260711: (dryRun) => api.post(`/admin/winner-sponsors/update-prizes-2026-07-11?dry_run=${dryRun}`),
};

// Sponsor convergence — unified allocation table (finalists + winners).
export const sponsorAllocationsAPI = {
  list: () => api.get('/admin/sponsor-allocations'),
  upsert: (payload) => api.post('/admin/sponsor-allocations', payload),
  patch: (id, payload) => api.patch(`/admin/sponsor-allocations/${id}`, payload),
  remove: (id) => api.delete(`/admin/sponsor-allocations/${id}`),
  migrate: (dryRun) => api.post(`/admin/sponsor-allocations/migrate?dry_run=${dryRun}`),
  parity: () => api.get('/admin/sponsor-allocations/parity'),
  diagnose: () => api.get('/admin/sponsor-allocations/diagnose'),
  rollback: (snapshotId) => api.post(`/admin/sponsor-allocations/rollback?snapshot_id=${snapshotId}`),
  updateProfileBg: (id, logo_background) => api.patch(`/admin/sponsor-profiles/${id}`, { logo_background }),
};


// Étape D — Finalist assets (certificate + badge). No email trigger here.
export const finalistAssetsAPI = {
  status: () => api.get('/admin/finalist-assets/status'),
  generate: (body) => api.post('/admin/finalist-assets/generate', body),
  generateStart: (body) => api.post('/admin/finalist-assets/generate/start', body),
  generateStatus: () => api.get('/admin/finalist-assets/generate/status'),
  preview: (submissionId) => api.get(`/admin/finalist-assets/preview/${submissionId}`),
  withAssets: () => api.get('/admin/finalist-assets/with-assets'),
};

